import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, Package, ShoppingCart,
  Loader2, ArrowUpRight, ArrowDownRight, Receipt, Truck, PieChart,
  Calendar, Download, AlertTriangle, CheckCircle2, BarChart3, Target
} from 'lucide-react';
import { fmtCurrency } from '../lib/localeUtils';
import { exportToPrintablePDF } from '../lib/exportUtils';

export default function FinancePage() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [financials, setFinancials] = useState(null);
  const [profitability, setProfitability] = useState(null);

  const fetchFinancials = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryRes, profitRes] = await Promise.all([
        api.get('/accounting/financial-summary', { params: { period } }),
        api.get('/accounting/product-profitability', { params: { period } })
      ]);
      setFinancials(summaryRes.data);
      setProfitability(profitRes.data);
    } catch (err) {
      console.error('Financials fetch error:', err);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchFinancials();
  }, [fetchFinancials]);

  const formatCurrency = (amount) => {
    return fmtCurrency(amount || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const handleExport = () => {
    const summaryCards = [
      { label: 'Revenue', value: formatCurrency(f.revenue?.total) },
      { label: 'COGS', value: formatCurrency(f.cogs?.total) },
      { label: 'Gross Profit', value: formatCurrency(f.gross_profit?.amount) },
      { label: 'Net Profit', value: formatCurrency(f.net_profit?.amount) },
    ];
    const headers = ['Product', 'Revenue', 'COGS', 'Gross Profit', 'Margin %', 'Units Sold'];
    const rows = (p.products || []).map(prod => [
      prod.name || '',
      formatCurrency(prod.revenue),
      formatCurrency(prod.cogs),
      formatCurrency(prod.gross_profit),
      formatPercent(prod.margin_percent),
      prod.units_sold || 0,
    ]);
    exportToPrintablePDF({
      title: `Financial Report - ${period.charAt(0).toUpperCase() + period.slice(1)}`,
      subtitle: 'Profit & Loss with Product Profitability',
      summaryCards,
      headers,
      rows,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const f = financials || {};
  const p = profitability || {};

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Financial Accounting
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Actual COGS tracking with weighted average cost
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]" data-testid="period-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-2" onClick={handleExport} data-testid="export-btn">
            <Download className="h-4 w-4" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Key Financial Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card data-testid="revenue-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs font-medium">Revenue</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(f.revenue?.total)}</p>
            <p className="text-xs text-muted-foreground mt-1">{f.revenue?.transactions || 0} transactions</p>
          </CardContent>
        </Card>

        <Card data-testid="cogs-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Package className="h-4 w-4" />
              <span className="text-xs font-medium">COGS</span>
            </div>
            <p className="text-2xl font-bold text-rose-600">{formatCurrency(f.cogs?.total)}</p>
            <Badge variant="outline" className="text-[10px] mt-1">
              Weighted Avg Cost
            </Badge>
          </CardContent>
        </Card>

        <Card data-testid="gross-profit-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs font-medium">Gross Profit</span>
            </div>
            <p className="text-2xl font-bold text-primary">{formatCurrency(f.gross_profit?.amount)}</p>
            <Badge variant="outline" className="mt-1 text-[10px] bg-primary/10 text-primary border-primary/30">
              {formatPercent(f.gross_profit?.margin_percent)} margin
            </Badge>
          </CardContent>
        </Card>

        <Card data-testid="net-profit-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Target className="h-4 w-4" />
              <span className="text-xs font-medium">Net Profit</span>
            </div>
            <p className={`text-2xl font-bold ${(f.net_profit?.amount || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {formatCurrency(f.net_profit?.amount)}
            </p>
            <Badge variant="outline" className="mt-1 text-[10px]">
              {formatPercent(f.net_profit?.margin_percent)} margin
            </Badge>
          </CardContent>
        </Card>

        <Card data-testid="inventory-cost-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Truck className="h-4 w-4" />
              <span className="text-xs font-medium">Inventory</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(f.inventory?.value_at_cost)}</p>
            <p className="text-xs text-muted-foreground mt-1">At cost</p>
          </CardContent>
        </Card>

        <Card data-testid="avg-order-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <ShoppingCart className="h-4 w-4" />
              <span className="text-xs font-medium">Avg Order</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(f.revenue?.average_order)}</p>
            <p className="text-xs text-muted-foreground mt-1">Per transaction</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pnl" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pnl" className="gap-2">
            <PieChart className="h-4 w-4" /> P&L Summary
          </TabsTrigger>
          <TabsTrigger value="products" className="gap-2">
            <BarChart3 className="h-4 w-4" /> Product Profitability
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2">
            <AlertTriangle className="h-4 w-4" /> Margin Alerts
          </TabsTrigger>
        </TabsList>

        {/* P&L Summary Tab */}
        <TabsContent value="pnl" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card data-testid="pnl-statement-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <PieChart className="h-4 w-4 text-primary" /> Profit & Loss Statement
                </CardTitle>
                <CardDescription>Based on actual cost tracking</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm font-medium">Sales Revenue</span>
                    <span className="text-sm font-semibold text-emerald-600">{formatCurrency(f.revenue?.total)}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b pl-4">
                    <span className="text-sm text-muted-foreground">(-) Discounts Given</span>
                    <span className="text-sm text-rose-600">({formatCurrency(f.expenses?.discounts)})</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b bg-muted/30 px-2 rounded">
                    <span className="text-sm font-medium">Net Revenue</span>
                    <span className="text-sm font-semibold">{formatCurrency((f.revenue?.total || 0) - (f.expenses?.discounts || 0))}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b pl-4">
                    <span className="text-sm text-muted-foreground">(-) Cost of Goods Sold</span>
                    <span className="text-sm text-rose-600">({formatCurrency(f.cogs?.total)})</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b bg-primary/10 px-2 rounded">
                    <span className="text-sm font-semibold">Gross Profit</span>
                    <span className="text-sm font-bold text-primary">{formatCurrency(f.gross_profit?.amount)}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 bg-emerald-500/10 px-3 rounded-lg mt-4">
                    <span className="text-sm font-bold">Net Profit</span>
                    <span className={`text-lg font-bold ${(f.net_profit?.amount || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(f.net_profit?.amount)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="inventory-summary-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" /> Inventory Valuation
                </CardTitle>
                <CardDescription>Current stock value analysis</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">At Cost</p>
                    <p className="text-xl font-bold">{formatCurrency(f.inventory?.value_at_cost)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Purchase price</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">At Retail</p>
                    <p className="text-xl font-bold">{formatCurrency(f.inventory?.value_at_retail)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Selling price</p>
                  </div>
                </div>
                <Separator />
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <p className="text-xs text-emerald-600 font-medium">Potential Profit in Inventory</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(f.inventory?.potential_profit)}</p>
                  <p className="text-xs text-muted-foreground mt-1">If all stock is sold at retail</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Period Purchases</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Total Orders</span>
                    <span>{f.expenses?.purchase_orders || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Amount</span>
                    <span>{formatCurrency(f.expenses?.purchases_in_period)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Profitable Products */}
          <Card data-testid="top-products-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" /> Top Profitable Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[200px]">
                <div className="space-y-2">
                  {(f.top_profitable_products || []).map((product, i) => {
                    const margin = product.revenue > 0 ? (product.profit / product.revenue) * 100 : 0;
                    return (
                      <div key={product.product_id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-bold text-muted-foreground w-6">#{i + 1}</span>
                          <div>
                            <p className="text-sm font-medium">{product.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {product.quantity} sold • {formatCurrency(product.revenue)} revenue
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-emerald-600">{formatCurrency(product.profit)}</p>
                          <Badge variant="outline" className="text-[10px]">{margin.toFixed(1)}% margin</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Product Profitability Tab */}
        <TabsContent value="products">
          <Card data-testid="product-profitability-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-primary" /> Product Profitability Analysis
              </CardTitle>
              <CardDescription>
                {p.summary?.products_analyzed || 0} products analyzed • Overall margin: {formatPercent(p.summary?.overall_margin)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4 mb-4 text-sm">
                <div className="p-3 rounded-lg bg-emerald-500/10 text-center">
                  <p className="text-xs text-muted-foreground">Total Revenue</p>
                  <p className="font-bold text-emerald-600">{formatCurrency(p.summary?.total_revenue)}</p>
                </div>
                <div className="p-3 rounded-lg bg-rose-500/10 text-center">
                  <p className="text-xs text-muted-foreground">Total COGS</p>
                  <p className="font-bold text-rose-600">{formatCurrency(p.summary?.total_cogs)}</p>
                </div>
                <div className="p-3 rounded-lg bg-primary/10 text-center">
                  <p className="text-xs text-muted-foreground">Total Profit</p>
                  <p className="font-bold text-primary">{formatCurrency(p.summary?.total_profit)}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-xs text-muted-foreground">Products</p>
                  <p className="font-bold">{p.summary?.products_analyzed || 0}</p>
                </div>
              </div>
              <ScrollArea className="h-[350px]">
                <div className="space-y-2">
                  {(p.products || []).map((product) => {
                    const marginColor = product.margin_percent >= 30 ? 'text-emerald-600' : 
                                       product.margin_percent >= 20 ? 'text-amber-600' : 'text-rose-600';
                    return (
                      <div key={product.product_id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/30 transition-colors">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{product.product_name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span>{product.category}</span>
                            <span>•</span>
                            <span>{product.quantity_sold} sold</span>
                            <span>•</span>
                            <span>Stock: {product.current_stock}</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-right text-sm ml-4">
                          <div>
                            <p className="text-xs text-muted-foreground">Revenue</p>
                            <p className="font-medium">{formatCurrency(product.revenue)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">COGS</p>
                            <p className="font-medium text-rose-600">{formatCurrency(product.cogs)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Profit</p>
                            <p className="font-semibold text-emerald-600">{formatCurrency(product.profit)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Margin</p>
                            <p className={`font-bold ${marginColor}`}>{formatPercent(product.margin_percent)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Margin Alerts Tab */}
        <TabsContent value="alerts">
          <Card data-testid="margin-alerts-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-4 w-4" /> Low Margin Products
              </CardTitle>
              <CardDescription>Products with margin below 20% - consider repricing</CardDescription>
            </CardHeader>
            <CardContent>
              {(f.low_margin_products || []).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                  <p>All products have healthy margins!</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-3">
                    {(f.low_margin_products || []).map((product) => (
                      <div key={product.product_id} className="flex items-center justify-between p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                          <div>
                            <p className="text-sm font-medium">{product.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Revenue: {formatCurrency(product.revenue)} • Profit: {formatCurrency(product.profit)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="destructive" className="text-sm">
                            {formatPercent(product.margin)} margin
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">Target: 20%+</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
